'use client';

import { tracking } from '@/lib/features/tracking';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    AgentModes,
    CHAT_STATES,
    DispatchType,
    ROLE_TYPES,
    STATUS_MESSAGES,
    STREAMING_CONSTANTS,
    STREAMING_EVENT_TYPES,
    TEMP_MESSAGE_IDS,
} from '../constants';
import { studioAIService, StudioAIService } from '../services';
import type {
    AgentMode,
    AgentOptionsPayload,
    ChatMessage,
    ChatMessageContent,
    PlannerFormState,
    StudioAIState,
} from '../types';
import { convertToSnowflakeFormat } from '../utils';
import { generateConversationId, isPendingAiId } from '../utils/message-utils';
import { conversationViewAsMismatch, type ViewAsConversationTag } from '../utils/view-as-dani';
import type { StudioAIAction } from './useChatReducer';

/**
 * Scan loaded messages for unanswered agent_options on the last assistant message.
 * If found (and no subsequent user message answered them), restore to UI state.
 */
function restorePendingOptions(
    messages: ChatMessage[],
    setPendingOptions: (options: AgentOptionsPayload | null) => void,
): void {
    if (!messages.length) {
        setPendingOptions(null);
        return;
    }

    const lastMsg = messages[messages.length - 1];

    if (lastMsg.role !== ROLE_TYPES.ASSISTANT || !Array.isArray(lastMsg.content)) {
        setPendingOptions(null);
        return;
    }

    const contentArray = lastMsg.content as ChatMessageContent[];
    const optionsItem = contentArray
        .slice()
        .reverse()
        .find((c) => c.type === STREAMING_EVENT_TYPES.AGENT_OPTIONS && c.agent_options);

    if (optionsItem?.agent_options) {
        setPendingOptions(optionsItem.agent_options);
    } else {
        setPendingOptions(null);
    }
}

export interface ConversationLifecycleConfig {
    dispatch: React.Dispatch<StudioAIAction>;
    state: StudioAIState;
    router: AppRouterInstance;
    canNavigateRef: React.RefObject<boolean>;
    setCurrentConversationId: (id: string | null) => void;
    setServerConversationOwner: (conversationId: string, owner: boolean) => void;
    /** The DANI-only picker's client. Under staff View-as DANI's client is derived instead. */
    setMasqueradeClient: (client: { id: string; name: string } | null) => void;
    /**
     * Staff View-as as it stands when a conversation loads (M4S-5670 §4): the
     * tab's client, and whether the user has the View-as control.
     */
    viewAsBindingRef: {
        readonly current: {
            isGlobalViewAsActive: boolean;
            targetSfAccountId: string | null;
            hasViewAsControl: boolean;
        };
    };
    /**
     * Who the open conversation was run as. The context derives the mismatch
     * lock from it, so the lock follows View-as and the flags as they settle.
     */
    setViewAsConversationTag: (tag: ViewAsConversationTag | null) => void;
    setAgentMode: (mode: AgentMode) => void;
    setPlannerFormState: (state: PlannerFormState | null) => void;
    setPlannerRunId: (runId: string | null) => void;
    setPlannerPhase: (phase: 'report' | null) => void;
    setPlannerTemplateId: (templateId: number | null) => void;
    setPlannerReportData: (
        data: { status?: string; reportId?: number; reportUrl?: string } | null,
    ) => void;
    setIsSharedView: (shared: boolean) => void;
    setPendingOptions: (options: AgentOptionsPayload | null) => void;
    setSmartRoutingEnabled: (enabled: boolean) => void;
    duplicateMessagesRef: React.RefObject<
        Array<{ role: 'user' | 'assistant'; content: Array<{ type: string; text: string }> }>
    >;
}

export function useConversationLifecycle(config: ConversationLifecycleConfig) {
    const {
        dispatch,
        state,
        router,
        canNavigateRef,
        setCurrentConversationId,
        setServerConversationOwner,
        setMasqueradeClient,
        viewAsBindingRef,
        setViewAsConversationTag,
        setAgentMode,
        setPlannerFormState,
        setPlannerRunId,
        setPlannerPhase,
        setPlannerTemplateId,
        setPlannerReportData,
        setIsSharedView,
        setPendingOptions,
        setSmartRoutingEnabled,
        duplicateMessagesRef,
    } = config;

    const loadMessagesRequestIdRef = useRef(0);
    const isNavigatingToNewChatRef = useRef(false);
    /** The unsaved chat `seedConversation` is navigating to; the URL sync effect waits for it. */
    const pendingSeedIdRef = useRef<string | null>(null);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current !== null) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    useEffect(() => stopPolling, [stopPolling]);

    const loadConversationMessages = useCallback(
        (conversationId: string, onError?: () => void) => {
            stopPolling();
            setCurrentConversationId(conversationId);
            setViewAsConversationTag(null);
            // Clear message-related state immediately so the UI doesn't briefly show
            // the previous conversation's messages while the new history is loading.
            dispatch({ type: DispatchType.SetMessages, payload: [] });
            dispatch({ type: DispatchType.SetThreadId, payload: null });
            dispatch({ type: DispatchType.SetParentMessageId, payload: '0' });
            // Reset streaming/loading state in case the caller didn't abort a pending
            // stream before calling this (e.g. direct calls outside the URL sync effect).
            dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.IDLE });
            dispatch({ type: DispatchType.SetLoading, payload: false });
            dispatch({ type: DispatchType.SetCurrentStatus, payload: null });
            // Messages queued for another chat's first question (Duplicate,
            // fork, seedConversation) must not ride into this one.
            duplicateMessagesRef.current = [];

            const fetchHistory = async () => {
                const requestId = ++loadMessagesRequestIdRef.current;
                try {
                    const result = await studioAIService.getConversationHistory(conversationId);

                    if (requestId !== loadMessagesRequestIdRef.current) return;

                    if (result) {
                        setServerConversationOwner(conversationId, result.owner ?? false);
                        const isShared = result.shared ?? false;
                        setIsSharedView(isShared);

                        // A conversation run as someone other than the tab's
                        // client opens locked instead of silently switching
                        // DANI's client (M4S-5670 §4 "Mismatch lock").
                        const tag: ViewAsConversationTag = {
                            masqueradeOrgId: result.masqueradeOrgId || null,
                            masqueradeOrgName: result.masqueradeOrgName || null,
                            isOwner: result.owner === true,
                        };
                        setViewAsConversationTag(tag);
                        const mismatch = conversationViewAsMismatch({
                            conversation: tag,
                            isOwner: tag.isOwner,
                            ...viewAsBindingRef.current,
                        });
                        if (mismatch) {
                            setMasqueradeClient(null);
                        } else if (result.masqueradeOrgId) {
                            setMasqueradeClient({
                                id: result.masqueradeOrgId,
                                name: result.masqueradeOrgName || result.masqueradeOrgId,
                            });
                        } else {
                            setMasqueradeClient(null);
                        }

                        // A plan-mode conversation reopens in plan mode. Under
                        // View-as with reports as a client off, the context
                        // locks its composer instead of sending to /chat.
                        if (result.plannerMode) {
                            setAgentMode(AgentModes.PLANNER);
                            setSmartRoutingEnabled(true);
                        } else {
                            setAgentMode(null);
                            setSmartRoutingEnabled(false);
                        }

                        setPlannerFormState(result.plannerFormState ?? null);
                        // Rehydrate the active plan template id so the UI keeps
                        // forwarding it on subsequent plan-mode sends.
                        const restoredTemplateId =
                            result.templateId != null
                                ? Number(result.templateId)
                                : result.plannerFormState?.templateId != null
                                  ? Number(result.plannerFormState.templateId)
                                  : null;
                        setPlannerTemplateId(
                            Number.isFinite(restoredTemplateId)
                                ? (restoredTemplateId as number)
                                : null,
                        );

                        // Hydrate the live plan sidebar snapshot from the
                        // conversation record. When absent (freestyle runs
                        // with no template adopted), clear any stale state.
                        dispatch({
                            type: DispatchType.SetPlanProgress,
                            payload: result.planProgress ?? null,
                        });

                        dispatch({
                            type: DispatchType.SetConversationContextData,
                            payload: result.contextData ?? null,
                        });

                        const TERMINAL_STATUSES = new Set(['COMPLETE', 'FAILED']);
                        if (result.runId) {
                            setPlannerRunId(result.runId);
                            if (result.reportStatus || result.reportId) {
                                setPlannerReportData({
                                    status: result.reportStatus,
                                    reportId: result.reportId,
                                    reportUrl: result.reportUrl,
                                });
                            } else {
                                setPlannerReportData(null);
                            }
                            if (result.reportStatus && TERMINAL_STATUSES.has(result.reportStatus)) {
                                // Already in terminal state — no polling needed
                            }
                        } else {
                            setPlannerRunId(null);
                            setPlannerPhase(null);
                            setPlannerReportData(null);
                        }

                        const messages = result.messages || [];
                        dispatch({ type: DispatchType.SetMessages, payload: messages });

                        const isGenerating = result.generatingResponseAt
                            ? Date.now() - new Date(result.generatingResponseAt).getTime() <
                              STREAMING_CONSTANTS.GENERATING_RESPONSE_TIMEOUT_MS
                            : false;

                        if (isGenerating) {
                            dispatch({ type: DispatchType.SetLoading, payload: true });
                            dispatch({
                                type: DispatchType.SetChatState,
                                payload: CHAT_STATES.LOADING,
                            });
                            dispatch({
                                type: DispatchType.SetCurrentStatus,
                                payload: `${STATUS_MESSAGES.THINKING.message}|${STATUS_MESSAGES.THINKING.icon}`,
                            });

                            const capturedId = requestId;
                            pollingIntervalRef.current = setInterval(async () => {
                                if (capturedId !== loadMessagesRequestIdRef.current) {
                                    stopPolling();
                                    return;
                                }
                                try {
                                    const poll =
                                        await studioAIService.getConversationHistory(
                                            conversationId,
                                        );
                                    if (capturedId !== loadMessagesRequestIdRef.current) {
                                        stopPolling();
                                        return;
                                    }
                                    const stillGenerating = poll?.generatingResponseAt
                                        ? Date.now() -
                                              new Date(poll.generatingResponseAt).getTime() <
                                          STREAMING_CONSTANTS.GENERATING_RESPONSE_TIMEOUT_MS
                                        : false;

                                    if (!stillGenerating) {
                                        stopPolling();
                                        const updatedMessages = poll?.messages ?? [];
                                        dispatch({
                                            type: DispatchType.SetMessages,
                                            payload: updatedMessages,
                                        });
                                        dispatch({ type: DispatchType.SetLoading, payload: false });
                                        dispatch({
                                            type: DispatchType.SetChatState,
                                            payload: CHAT_STATES.IDLE,
                                        });
                                        dispatch({
                                            type: DispatchType.SetCurrentStatus,
                                            payload: null,
                                        });
                                        restorePendingOptions(updatedMessages, setPendingOptions);
                                        if (poll?.threadId) {
                                            dispatch({
                                                type: DispatchType.SetThreadId,
                                                payload: poll.threadId.toString(),
                                            });
                                        }
                                        const lastAssistant = updatedMessages
                                            .slice()
                                            .reverse()
                                            .find(
                                                (m: ChatMessage) => m.role === ROLE_TYPES.ASSISTANT,
                                            );
                                        if (
                                            lastAssistant?.id &&
                                            !isPendingAiId(lastAssistant.id) &&
                                            lastAssistant.id !== TEMP_MESSAGE_IDS.PENDING_USER
                                        ) {
                                            dispatch({
                                                type: DispatchType.SetParentMessageId,
                                                payload: lastAssistant.id,
                                            });
                                        }
                                    }
                                } catch {
                                    // transient poll error — keep retrying on next tick
                                }
                                // Poll every 5 seconds, which is a balance between timely updates and avoiding excessive requests.
                            }, 5000);
                        }

                        restorePendingOptions(messages, setPendingOptions);

                        if (result.threadId) {
                            dispatch({
                                type: DispatchType.SetThreadId,
                                payload: result.threadId.toString(),
                            });
                        }

                        if (messages.length > 0) {
                            const lastAssistantMessage = messages
                                .slice()
                                .reverse()
                                .find((msg: ChatMessage) => msg.role === ROLE_TYPES.ASSISTANT);

                            if (lastAssistantMessage && lastAssistantMessage.id) {
                                const lastAssistantId = lastAssistantMessage.id;
                                const isTempId =
                                    isPendingAiId(lastAssistantId) ||
                                    lastAssistantId === TEMP_MESSAGE_IDS.PENDING_USER;
                                if (!isTempId) {
                                    dispatch({
                                        type: DispatchType.SetParentMessageId,
                                        payload: lastAssistantId,
                                    });
                                }
                            }
                        }
                    }
                } catch (error) {
                    // The DANI-only picker's client only: under View-as the
                    // derived client survives a failed load.
                    setMasqueradeClient(null);
                    setViewAsConversationTag(null);
                    tracking.errorWithSource(error as Error, 'Context.tsx', {
                        conversationId,
                    });
                    if (requestId !== loadMessagesRequestIdRef.current) return;
                    dispatch({
                        type: DispatchType.SetError,
                        payload: (error as Error).message,
                    });
                    onError?.();
                }
            };

            fetchHistory();
        },
        [
            dispatch,
            setCurrentConversationId,
            setServerConversationOwner,
            setMasqueradeClient,
            viewAsBindingRef,
            setViewAsConversationTag,
            setAgentMode,
            setPlannerFormState,
            setPlannerRunId,
            setPlannerPhase,
            setPlannerTemplateId,
            setPlannerReportData,
            setIsSharedView,
            setPendingOptions,
            setSmartRoutingEnabled,
            stopPolling,
            duplicateMessagesRef,
        ],
    );

    const openConversation = useCallback(
        (conversationId: string) => {
            if (!canNavigateRef.current) return;
            const currentPath = window.location.pathname;
            if (currentPath.includes('/dani')) {
                router.push(`/dani/${conversationId}`, { scroll: false });
            } else {
                router.push(`/dani/${conversationId}`);
            }
        },
        [router, canNavigateRef],
    );

    /** `href` lands the new chat somewhere other than the blank /dani page,
     * e.g. a Connect use case's questions. */
    const startNewConversation = useCallback(
        (href = '/dani') => {
            stopPolling();
            // Drop any in-flight history fetch (e.g. user opened a conversation then
            // switched org before getConversationHistory resolved) so it cannot
            // restore the old thread after we clear state.
            loadMessagesRequestIdRef.current += 1;
            setCurrentConversationId(null);
            dispatch({ type: DispatchType.SetMessages, payload: [] });
            dispatch({ type: DispatchType.SetThreadId, payload: null });
            dispatch({ type: DispatchType.SetParentMessageId, payload: '0' });
            dispatch({ type: DispatchType.SetLoading, payload: false });
            dispatch({ type: DispatchType.SetCurrentStatus, payload: null });
            dispatch({ type: DispatchType.SetError, payload: null });
            dispatch({ type: DispatchType.SetPlanProgress, payload: null });
            setMasqueradeClient(null);
            setViewAsConversationTag(null);
            setAgentMode(null);
            setPlannerFormState(null);
            setPlannerRunId(null);
            setPlannerPhase(null);
            setPlannerTemplateId(null);
            setPlannerReportData(null);
            setIsSharedView(false);
            setPendingOptions(null);
            setSmartRoutingEnabled(false);
            // A new chat starts empty, whatever another chat had queued.
            duplicateMessagesRef.current = [];
            pendingSeedIdRef.current = null;
            if (canNavigateRef.current) {
                isNavigatingToNewChatRef.current = true;
                router.push(href, { scroll: false });
            }
        },
        [
            router,
            dispatch,
            setCurrentConversationId,
            setMasqueradeClient,
            setViewAsConversationTag,
            setAgentMode,
            setPlannerFormState,
            setPlannerRunId,
            setPlannerPhase,
            setPlannerTemplateId,
            setPlannerReportData,
            setIsSharedView,
            setPendingOptions,
            setSmartRoutingEnabled,
            canNavigateRef,
            stopPolling,
            duplicateMessagesRef,
        ],
    );

    const duplicateConversation = useCallback(
        async (conversationId: string) => {
            try {
                const result = await studioAIService.getConversationHistory(conversationId);
                if (!result || !result.messages || result.messages.length === 0) {
                    dispatch({
                        type: DispatchType.SetError,
                        payload: 'No messages found to duplicate',
                    });
                    return;
                }

                const messages = result.messages as ChatMessage[];
                const snowflakeMessages = convertToSnowflakeFormat(messages) as Array<{
                    role: 'user' | 'assistant';
                    content: Array<{ type: string; text: string }>;
                }>;

                duplicateMessagesRef.current = snowflakeMessages;

                setCurrentConversationId(null);
                dispatch({ type: DispatchType.SetThreadId, payload: null });
                dispatch({ type: DispatchType.SetParentMessageId, payload: '0' });
                setIsSharedView(false);
                setViewAsConversationTag(null);
                setPlannerRunId(null);
                setPlannerPhase(null);
                setPlannerTemplateId(null);
                setPlannerReportData(null);
                setPlannerFormState(null);
                setAgentMode(null);

                dispatch({ type: DispatchType.SetMessages, payload: messages });
                dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.IDLE });

                if (canNavigateRef.current) {
                    const isAlreadyAtDani =
                        window.location.pathname === '/dani' ||
                        window.location.pathname === '/dani/';
                    if (!isAlreadyAtDani) {
                        isNavigatingToNewChatRef.current = true;
                        router.push('/dani', { scroll: false });
                    }
                }
            } catch (error) {
                tracking.errorWithSource(error as Error, 'Context.tsx', {
                    action: 'duplicateConversation',
                    conversationId,
                });
                dispatch({
                    type: DispatchType.SetError,
                    payload: `Failed to duplicate conversation: ${(error as Error).message}`,
                });
            }
        },
        [
            router,
            dispatch,
            duplicateMessagesRef,
            setCurrentConversationId,
            setIsSharedView,
            setViewAsConversationTag,
            setPlannerRunId,
            setPlannerPhase,
            setPlannerTemplateId,
            setPlannerReportData,
            setPlannerFormState,
            setAgentMode,
            canNavigateRef,
        ],
    );

    const forkConversationFromMessage = useCallback(
        (messageId: string) => {
            const currentMessages = state.messages;
            const targetIndex = currentMessages.findIndex((m) => m.id === messageId);
            if (targetIndex === -1) {
                dispatch({
                    type: DispatchType.SetError,
                    payload: 'Could not find the target message to fork from',
                });
                return;
            }

            const slicedMessages = currentMessages.slice(0, targetIndex + 1);
            const snowflakeMessages = convertToSnowflakeFormat(slicedMessages) as Array<{
                role: 'user' | 'assistant';
                content: Array<{ type: string; text: string }>;
            }>;

            duplicateMessagesRef.current = snowflakeMessages;

            const newConversationId = generateConversationId();
            setCurrentConversationId(newConversationId);
            dispatch({ type: DispatchType.SetThreadId, payload: null });
            dispatch({ type: DispatchType.SetParentMessageId, payload: '0' });
            setIsSharedView(false);
            setViewAsConversationTag(null);
            setPlannerRunId(null);
            setPlannerPhase(null);
            setPlannerTemplateId(null);
            setPlannerReportData(null);
            setPlannerFormState(null);
            setAgentMode(null);

            dispatch({ type: DispatchType.SetMessages, payload: slicedMessages });
            dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.IDLE });

            if (canNavigateRef.current) {
                isNavigatingToNewChatRef.current = true;
                router.push(`/dani/${newConversationId}`, { scroll: false });
            }
        },
        [
            state.messages,
            router,
            dispatch,
            duplicateMessagesRef,
            setCurrentConversationId,
            setIsSharedView,
            setViewAsConversationTag,
            setPlannerRunId,
            setPlannerPhase,
            setPlannerTemplateId,
            setPlannerReportData,
            setPlannerFormState,
            setAgentMode,
            canNavigateRef,
        ],
    );

    /**
     * Open a new, unsaved chat that already shows `messages` and hands them to
     * DANI with the first question (`previousMessages`), as Duplicate and fork
     * do; DANI saves it then. An insight card's Dive deep lands here from the
     * /dani landing (docs/pdlc/insights-dive-deep.md §3.3),
     * so it marks `pendingSeedIdRef` rather than `isNavigatingToNewChatRef`:
     * the URL sync effect then waits for the new URL instead of resetting on
     * the landing page, and never loads the unsaved chat. Replace, not push,
     * so Back returns to where the click came from.
     */
    const seedConversation = useCallback(
        (messages: ChatMessage[]) => {
            duplicateMessagesRef.current = convertToSnowflakeFormat(messages) as Array<{
                role: 'user' | 'assistant';
                content: Array<{ type: string; text: string }>;
            }>;

            const newConversationId = generateConversationId();
            setCurrentConversationId(newConversationId);
            dispatch({ type: DispatchType.SetThreadId, payload: null });
            dispatch({ type: DispatchType.SetParentMessageId, payload: '0' });
            setIsSharedView(false);
            setViewAsConversationTag(null);
            setPlannerRunId(null);
            setPlannerPhase(null);
            setPlannerTemplateId(null);
            setPlannerReportData(null);
            setPlannerFormState(null);
            setAgentMode(null);

            dispatch({ type: DispatchType.SetMessages, payload: messages });
            dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.IDLE });

            if (canNavigateRef.current) {
                pendingSeedIdRef.current = newConversationId;
                router.replace(`/dani/${newConversationId}`, { scroll: false });
            }
            return newConversationId;
        },
        [
            router,
            dispatch,
            duplicateMessagesRef,
            setCurrentConversationId,
            setIsSharedView,
            setViewAsConversationTag,
            setPlannerRunId,
            setPlannerPhase,
            setPlannerTemplateId,
            setPlannerReportData,
            setPlannerFormState,
            setAgentMode,
            canNavigateRef,
        ],
    );

    return useMemo(
        () => ({
            loadConversationMessages,
            openConversation,
            startNewConversation,
            duplicateConversation,
            forkConversationFromMessage,
            seedConversation,
            isNavigatingToNewChatRef,
            pendingSeedIdRef,
            stopPolling,
        }),
        [
            loadConversationMessages,
            openConversation,
            startNewConversation,
            duplicateConversation,
            forkConversationFromMessage,
            seedConversation,
            stopPolling,
        ],
    );
}
